import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { Record, List } from 'immutable';
import LegalConsentCheckbox from 'MeetingsBase/models/LegalConsentCheckbox';
import { IMPLICIT_PROCESSING_CONSENT_TYPE, LAWFUL_BASIS_PQL } from 'MeetingsBase/constants/legalConsent';
export var parseHtmlString = function parseHtmlString(htmlString) {
  return new DOMParser().parseFromString(htmlString, 'text/html');
};

var isEmptyHTML = function isEmptyHTML(text) {
  return parseHtmlString(text).body.innerText.trim() === '';
};

var isEmptyParagraph = function isEmptyParagraph(text) {
  return !text || text === '<p></p>' || isEmptyHTML(text);
};

function isLegalConsentObject(json) {
  return !!json.processingConsentText;
}

var defaults = {
  isLegitimateInterest: false,
  legitimateInterestPrivacyPolicyText: '',
  legitimateInterestSubscriptionTypes: List(),
  legitimateInterestLegalBasis: LAWFUL_BASIS_PQL,
  communicationConsentText: '',
  communicationConsentCheckboxes: List([new LegalConsentCheckbox()]),
  implicitProcessingConsentText: '',
  privacyPolicyText: ''
};

var LegalConsentOptions = /*#__PURE__*/function (_Record) {
  _inherits(LegalConsentOptions, _Record);

  function LegalConsentOptions() {
    _classCallCheck(this, LegalConsentOptions);

    return _possibleConstructorReturn(this, _getPrototypeOf(LegalConsentOptions).apply(this, arguments));
  }

  _createClass(LegalConsentOptions, [{
    key: "hasSubscription",
    value: function hasSubscription(id) {
      return Boolean(this.communicationConsentCheckboxes.find(function (subscription) {
        return subscription.communicationTypeId === id;
      }));
    }
  }, {
    key: "hasLegitimateInterestSubscription",
    value: function hasLegitimateInterestSubscription(id) {
      return this.legitimateInterestSubscriptionTypes.includes(id);
    }
  }, {
    key: "hasEmptySubscription",
    value: function hasEmptySubscription() {
      return Boolean(this.communicationConsentCheckboxes.find(function (subscription) {
        return !subscription.communicationTypeId;
      }));
    }
  }, {
    key: "getNumberOfSubscriptions",
    value: function getNumberOfSubscriptions() {
      return this.communicationConsentCheckboxes.size;
    }
  }, {
    key: "getSubscriptions",
    value: function getSubscriptions() {
      return this.communicationConsentCheckboxes;
    }
  }, {
    key: "getCheckboxLabels",
    value: function getCheckboxLabels() {
      return this.communicationConsentCheckboxes.map(function (checkbox) {
        return checkbox.label;
      });
    }
  }, {
    key: "convertToJSON",
    value: function convertToJSON() {
      var json = this.toJS();

      var legitimateInterestPrivacyPolicyText = json.legitimateInterestPrivacyPolicyText,
          implicitProcessingConsentText = json.implicitProcessingConsentText,
          restJson = _objectWithoutProperties(json, ["legitimateInterestPrivacyPolicyText", "implicitProcessingConsentText"]);

      var result = Object.assign({}, restJson, {
        processingConsentText: implicitProcessingConsentText,
        processingConsentType: IMPLICIT_PROCESSING_CONSENT_TYPE
      });
      /*
       * Forms saves legitimateInterestPrivacyPolicyText into privacyPolicyText
       * when isLegitimateInterest is true.
       */

      if (json.isLegitimateInterest) {
        result.privacyPolicyText = legitimateInterestPrivacyPolicyText;
      }

      return result;
    }
  }, {
    key: "createdFromNull",
    value: function createdFromNull() {
      return this.equals(LegalConsentOptions.from(null));
    }
  }, {
    key: "lacksSubscriptionId",
    value: function lacksSubscriptionId() {
      return this.communicationConsentCheckboxes && this.communicationConsentCheckboxes.first() && !this.communicationConsentCheckboxes.first().communicationTypeId || this.legitimateInterestSubscriptionTypes.isEmpty();
    }
  }, {
    key: "lacksLegitimateInterestText",
    value: function lacksLegitimateInterestText() {
      return !this.legitimateInterestPrivacyPolicyText;
    }
  }, {
    key: "allCheckboxesHaveLabels",
    value: function allCheckboxesHaveLabels() {
      return this.communicationConsentCheckboxes.every(function (checkbox) {
        return !isEmptyParagraph(checkbox.label);
      });
    }
  }, {
    key: "filterEmptyConsentSubscriptions",
    value: function filterEmptyConsentSubscriptions() {
      return this.update('communicationConsentCheckboxes', function (checkboxes) {
        return checkboxes.filter(function (checkbox) {
          return Boolean(checkbox.communicationTypeId);
        });
      });
    }
  }, {
    key: "filterEmptyLegitimateInterestSubscriptions",
    value: function filterEmptyLegitimateInterestSubscriptions() {
      return this.update('legitimateInterestSubscriptionTypes', function (subscriptions) {
        return subscriptions.filter(function (subscription) {
          return Boolean(subscription);
        });
      });
    }
  }], [{
    key: "from",
    value: function from(json) {
      if (json) {
        return new LegalConsentOptions({
          isLegitimateInterest: json.isLegitimateInterest || false,
          legitimateInterestPrivacyPolicyText: this.getLegitimateInterestText(json),
          legitimateInterestSubscriptionTypes: this.getLegitimateInterestSubscriptions(json),
          legitimateInterestLegalBasis: json.legitimateInterestLegalBasis || LAWFUL_BASIS_PQL,
          communicationConsentText: json.communicationConsentText,
          communicationConsentCheckboxes: this.getCheckboxConfig(json),
          privacyPolicyText: json.privacyPolicyText,
          implicitProcessingConsentText: this.getProcessingText(json)
        });
      }

      return new LegalConsentOptions();
    }
  }, {
    key: "getLegitimateInterestText",
    value: function getLegitimateInterestText(json) {
      if (json.isLegitimateInterest) {
        return json.privacyPolicyText;
      }

      return json.legitimateInterestPrivacyPolicyText;
    }
  }, {
    key: "getCheckboxConfig",
    value: function getCheckboxConfig(json) {
      var checkboxConfig = json.communicationConsentCheckboxes;

      if (checkboxConfig && checkboxConfig.map && json.communicationConsentCheckboxes) {
        return List(json.communicationConsentCheckboxes.map(function (checkbox) {
          return new LegalConsentCheckbox(checkbox);
        }));
      }

      return List([new LegalConsentCheckbox()]);
    }
  }, {
    key: "getLegitimateInterestSubscriptions",
    value: function getLegitimateInterestSubscriptions(json) {
      var subscriptionsConfig = json.legitimateInterestSubscriptionTypes;

      if (subscriptionsConfig && subscriptionsConfig.map) {
        return List(subscriptionsConfig);
      }

      return List();
    }
  }, {
    key: "getProcessingText",
    value: function getProcessingText(json) {
      if (isLegalConsentObject(json)) {
        return json.processingConsentText;
      }

      return json.implicitProcessingConsentText;
    }
  }]);

  return LegalConsentOptions;
}(Record(defaults));

export { LegalConsentOptions as default };